/**
 * Product-related Shopify Storefront fetchers.
 */
import { filterHiddenProducts, HIDDEN_PRODUCT_HANDLES, shopifyRequest } from "./client";
import type { ShopifyProduct } from "./types";

export async function fetchShopifyProducts(
  first: number = 20,
  cursor?: string
): Promise<{
  edges: { node: ShopifyProduct }[];
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
}> {
  const query = `
    query GetProducts($first: Int!, $after: String) {
      products(first: $first, after: $after, sortKey: CREATED_AT, reverse: true) {
        edges {
          node {
            id
            title
            description
            descriptionHtml
            handle
            createdAt
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            images(first: 1) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  availableForSale
                  image {
                    url
                    altText
                  }
                  selectedOptions {
                    name
                    value
                  }
                }
              }
            }
            options {
              name
              values
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, { first, after: cursor });
    return {
      edges: filterHiddenProducts(result.data.products.edges),
      pageInfo: result.data.products.pageInfo,
    };
  } catch (error) {
    console.error("Error fetching Shopify products:", error);
    return { edges: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }
}

export async function fetchProductsByCollection(
  collectionHandle: string,
  first: number = 50,
  cursor?: string
): Promise<{
  edges: { node: ShopifyProduct }[];
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
}> {
  const query = `
    query GetCollectionProducts($handle: String!, $first: Int!, $after: String) {
      collection(handle: $handle) {
        products(first: $first, after: $after, sortKey: COLLECTION_DEFAULT) {
          edges {
            node {
              id
              title
            description
            descriptionHtml
            handle
            createdAt
            tags
            priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              images(first: 1) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
              variants(first: 10) {
                edges {
                  node {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    availableForSale
                    image {
                      url
                      altText
                    }
                    selectedOptions {
                      name
                      value
                    }
                  }
                }
              }
              options {
                name
                values
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, { handle: collectionHandle, first, after: cursor });
    return {
      edges: filterHiddenProducts(result.data.collection?.products.edges || []),
      pageInfo: result.data.collection?.products.pageInfo || {
        hasNextPage: false,
        endCursor: null,
      },
    };
  } catch (error) {
    console.error("Error fetching collection products:", error);
    return { edges: [], pageInfo: { hasNextPage: false, endCursor: null } };
  }
}

export async function fetchProductByHandle(handle: string): Promise<ShopifyProduct | null> {
  const query = `
    query GetProduct($handle: String!) {
      productByHandle(handle: $handle) {
        id
        title
        description
        descriptionHtml
        handle
        productType
        tags
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
        images(first: 10) {
          edges {
            node {
              url
              altText
            }
          }
        }
        variants(first: 50) {
          edges {
            node {
              id
              title
              price {
                amount
                currencyCode
              }
              availableForSale
              image {
                url
                altText
              }
              selectedOptions {
                name
                value
              }
            }
          }
        }
        options {
          name
          values
        }
        collections(first: 5) {
          edges {
            node {
              id
              handle
              title
            }
          }
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, { handle });
    return result.data.productByHandle;
  } catch (error) {
    console.error("Error fetching product:", error);
    return null;
  }
}

/** Fetch related products from the same collections as the given product handles. */
export async function fetchRelatedProducts(
  productHandles: string[],
  excludeHandles: string[],
  maxProducts: number = 8
): Promise<ShopifyProduct[]> {
  if (productHandles.length === 0) return [];

  try {
    // Step 1: Get collections for each cart product
    const collectionHandles = new Set<string>();

    for (const handle of productHandles.slice(0, 4)) {
      // Limit to first 4 to avoid too many requests
      const product = await fetchProductByHandle(handle);
      if (product?.collections?.edges) {
        for (const edge of product.collections.edges) {
          collectionHandles.add(edge.node.handle);
        }
      }
    }

    if (collectionHandles.size === 0) return [];

    // Step 2: Fetch products from those collections
    const allProducts = new Map<string, ShopifyProduct>();
    const excludeSet = new Set([...excludeHandles, ...Array.from(HIDDEN_PRODUCT_HANDLES)]);

    for (const collHandle of Array.from(collectionHandles).slice(0, 3)) {
      const { edges } = await fetchProductsByCollection(collHandle, 10);
      for (const edge of edges) {
        const p = edge.node;
        if (!excludeSet.has(p.handle) && !allProducts.has(p.id)) {
          allProducts.set(p.id, p);
        }
      }
      if (allProducts.size >= maxProducts) break;
    }

    return Array.from(allProducts.values()).slice(0, maxProducts);
  } catch (error) {
    console.error("Error fetching related products:", error);
    return [];
  }
}
